import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import useLenis from "@/hooks/useLenis";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";

function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `$${n}`;
}
function fmtDays(low, high) {
  if (low === high || high === undefined) return `${low} d`;
  return `${low}–${high} d`;
}

/**
 * /what-we-collapse — the collapse ledger, its own tab.
 * Three named tiles: Cost Collapse · Time Collapse · Staff Collapse.
 * Numeric typography matches the site's font-display display-tier used on
 * the pledge card and the philanthropy headline: uppercase, clamped,
 * loud on the orange accent.
 */
export default function WhatWeCollapse() {
  useLenis();
  const [collapse, setCollapse] = useState([]);

  useEffect(() => {
    api.get("/transparency/collapse")
      .then((r) => setCollapse(r.data.metrics || []))
      .catch(() => {});
  }, []);

  return (
    <Layout>
      <div data-testid="page-what-we-collapse" className="relative bg-[#050505] text-[#ededed] min-h-screen">
        {/* Hero */}
        <section className="relative pt-32 sm:pt-40 pb-14 sm:pb-20 px-6 sm:px-10 max-w-[1200px] mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono text-[11px] uppercase tracking-[0.45em] text-[#ff5a00]"
          >
            The Public Record
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            data-testid="collapse-headline"
            className="font-display uppercase leading-[0.95] text-[#ededed] mt-6"
            style={{ fontSize: "clamp(2.75rem, 8vw, 6.5rem)", letterSpacing: "-0.005em" }}
          >
            What We
            <br />
            <span className="italic text-[#ff5a00]">Collapse.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="font-display italic text-lg sm:text-2xl text-[#a1a1aa] max-w-3xl mx-auto mt-8"
          >
            Three cuts, one ledger. Every price, every timeline, every headcount
            the traditional industry treats as fixed — collapsed on the record.
          </motion.p>
        </section>

        {/* THREE COLLAPSE TILES */}
        <section className="px-6 sm:px-10 max-w-[1400px] mx-auto pb-16 space-y-16">
          {/* COST COLLAPSE */}
          <CollapseTile
            testid="tile-cost-collapse"
            eyebrow="01 · Cost Collapse"
            title="Cost Collapse."
            subtitle="What the industry charges vs. what we charge."
            rows={collapse.map((m) => ({
              tier: m.tier,
              old: `${fmtMoney(m.old_price_low)}–${fmtMoney(m.old_price_high)}`,
              forge: fmtMoney(m.forge_price),
            }))}
          />

          {/* TIME COLLAPSE */}
          <CollapseTile
            testid="tile-time-collapse"
            eyebrow="02 · Time Collapse"
            title="Time Collapse."
            subtitle="What the industry takes vs. what we deliver."
            rows={collapse.map((m) => ({
              tier: m.tier,
              old: fmtDays(m.old_days_low, m.old_days_high),
              forge: `${m.forge_days} d`,
            }))}
          />

          {/* STAFF COLLAPSE */}
          <CollapseTile
            testid="tile-staff-collapse"
            eyebrow="03 · Staff Collapse"
            title="Staff Collapse."
            subtitle="What the industry staffs vs. how we ship."
            rows={collapse.map((m) => {
              const parts = (m.staff_collapse || "").split("→").map((s) => s.trim());
              return {
                tier: m.tier,
                old: parts[0] || m.staff_collapse,
                forge: parts.length > 1 ? parts[1] : "1",
              };
            })}
          />
        </section>

        {/* Footnote + back-link */}
        <section className="px-6 sm:px-10 max-w-[1200px] mx-auto pb-24 pt-4 text-center space-y-8">
          <p className="font-body text-sm text-[#71717a] italic max-w-2xl mx-auto leading-relaxed">
            All figures are the Forge's live ceilings — quoted, dated, and
            recoverable from any of our product pages. Numbers move in one
            direction only: down.
          </p>
          <Link
            to="/"
            data-testid="collapse-back-home"
            className="inline-block font-mono text-[10px] uppercase tracking-[0.42em] text-[#a1a1aa] hover:text-[#ff5a00] transition-colors border border-[#27272a] hover:border-[#ff5a00]/60 px-6 py-3"
          >
            ← Back to the Forge
          </Link>
        </section>
      </div>
    </Layout>
  );
}

function CollapseTile({ testid, eyebrow, title, subtitle, rows }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      data-testid={testid}
      className="border border-[#27272a] bg-[#0b0b0d]/70 backdrop-blur"
    >
      {/* Header */}
      <div className="px-6 sm:px-10 pt-8 sm:pt-10 pb-6 border-b border-[#27272a]">
        <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00]">
          {eyebrow}
        </p>
        <h2
          className="font-display uppercase leading-[0.9] text-[#ededed] mt-3"
          style={{ fontSize: "clamp(2rem, 5.5vw, 4rem)", letterSpacing: "-0.005em" }}
        >
          {title.replace(/\.$/, "")}
          <span className="text-[#ff5a00]">.</span>
        </h2>
        <p className="font-display italic text-base sm:text-xl text-[#a1a1aa] mt-3">
          {subtitle}
        </p>
      </div>

      {/* Column labels */}
      <div className="hidden md:grid grid-cols-3 px-6 sm:px-10 py-3 border-b border-[#27272a] font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
        <span>Tier</span>
        <span>Traditional</span>
        <span>Resilience</span>
      </div>

      {/* Rows */}
      <div>
        {rows.length === 0 && (
          <div className="px-6 sm:px-10 py-10 text-center font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
            Loading…
          </div>
        )}
        {rows.map((r, i) => (
          <div
            key={r.tier + i}
            data-testid={`${testid}-row-${i}`}
            className="grid grid-cols-1 md:grid-cols-3 gap-y-2 gap-x-6 items-baseline px-6 sm:px-10 py-6 border-b border-white/5 last:border-0 hover:bg-[#141417]/60 transition-colors"
          >
            {/* Tier — matches pledge/headline family: font-display uppercase */}
            <p
              className="font-display uppercase text-[#ededed] leading-tight"
              style={{ fontSize: "clamp(1.15rem, 1.6vw, 1.6rem)", letterSpacing: "-0.005em" }}
            >
              {r.tier}
            </p>
            {/* Traditional — struck-through, same display font */}
            <p
              className="font-display uppercase text-[#a1a1aa] line-through decoration-[#52525b]/60 leading-tight"
              style={{ fontSize: "clamp(1.15rem, 1.6vw, 1.6rem)", letterSpacing: "-0.005em" }}
            >
              {r.old}
            </p>
            {/* Resilience — loud, orange, same display font */}
            <p
              className="font-display uppercase text-[#ff5a00] leading-tight"
              style={{ fontSize: "clamp(1.25rem, 1.85vw, 1.9rem)", letterSpacing: "-0.005em" }}
            >
              {r.forge}
            </p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
